import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import template from './group-datasets.hbs'
import FormView from 'scoreboard/components/form-view'
import appDisplay from 'scoreboard/components/app-display'
import i18n from 'scoreboard/components/i18n'
import ObjectSelectorForm from 'scoreboard/components/object-selector-form'
import spotlight from 'scoreboard/components/spotlight'
import groupDatasetsItem from './group-datasets-item.hbs'
import DatasetPermissionsForm from 'scoreboard/datasets/dataset-permissions-form'
import Dataset from 'scoreboard/datasets/model/dataset'
import CovertButtonTooltips from 'scoreboard/components/covert-button-tooltips'
import conductor from 'scoreboard/components/conductor'
import config from 'scoreboard/components/config'
import Tooltip from 'scoreboard/components/tooltip'
import user from 'scoreboard/components/user'
import arrayToHash from 'scoreboard/components/array-to-hash'
import guard from 'scoreboard/components/unsaved/guard'
import * as datasetSelectorOptions from 'scoreboard/datasets/dataset-selector-options'
import projects from 'scoreboard/projects/projects'
import unwrapModel from 'scoreboard/components/unwrap-model'
import ConfirmPrompt from 'scoreboard/components/confirm-prompt'
import DependenciesPreventActionView from 'scoreboard/components/edit/dependencies-prevent-action-view'
import dialogStack from 'scoreboard/components/dialog-stack'
import DeleteConfirmationTooltip from 'scoreboard/components/delete-confirmation-tooltip'

var GroupDatasets = Backbone.View.extend({

	/**
	 * Dialog that displays all the datasets a group is allowed to view.  It is
	 * accessed from Admin Groups
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {Group} options.model the group
	 * @param {FormViewOptions} options.formViewOptions
	 */
	initialize: function(options) {
		this.options = options || {}
		this.formView = new FormView(_.extend({
			el: this.el,
			submit: this.submit.bind(this)
		}, options.formViewOptions))
		const {datasets, organizations, projects} = this.model.attributes
		this.datasets = _.sortBy(datasets, d => d.label.toLocaleLowerCase())
		this.datasetsById = _.keyBy(this.datasets, 'id')
		this.organizationsById = _.keyBy(organizations, 'id')
		this.projectsById = _.keyBy(projects, 'id')
		this.treeItemsById = this.options.treeItemsById
	},

	tagName: 'form',

	className: 'group-datasets',

	events: {
		'click .add': 'add',
		'click .edit-button': 'edit',
		'click .delete': 'deleteConfirmation',
		'click .explore': 'onClickExplore'
	},

	render: function() {
		this.$el.empty().append(template())
		_.each(this.datasets, function(dataset) {
			let datasetItemContext = this.groupDatasetsItemContext(dataset)
			this.$('.list').append(groupDatasetsItem(datasetItemContext))
		}.bind(this))
		this.toggleNoneMessage()
		this.formView.start()
		this.covertButtonTooltips = new CovertButtonTooltips({ el: this.el })
		return this
	},

	toggleNoneMessage: function() {
		this.$('.no-datasets').toggleClass('is-not-displayed', !_.isEmpty(this.datasets))
	},

	add: function() {
		let selector = new ObjectSelectorForm(_.extend(datasetSelectorOptions.forSelector(), {
			title: i18n.get('common.add2', 'datasets.dataset'),
			primaryButtonKey: 'common.add'
		}))

		appDisplay.dialog(selector)

		selector.on('select', (dataset, org) => {

			var closeSelector = (doSpotlight=true) => {
				// spotlight the panel containing the dataset
				// Wait for the next animation frame, though, or the background
				// transition won't work
				if (doSpotlight) {
					window.requestAnimationFrame(() =>
						spotlight(this.$('li[data-id=' + dataset.id + ']')))
				}
				selector.trigger('done')
			}

			if (!dataset.permissions) { // going from standard to advanced permission
				if (!dataset.administer) {
					selector.formView.errorMessage(i18n.get('admin.group.datasetPermissions.needAdministerForAdvanced'))
					return
				}
				const prompt = new ConfirmPrompt({
					confirmLabel: 'common.useAdvancedPermissions',
					cancelLabel: 'common.cancel',
					message: i18n.get('admin.group.advancedPermissionPrompt')
				})
				prompt.on('done', () => this.addSelectedDataset(dataset, org, closeSelector))
				prompt.on('cancel', () => selector.reset())
				appDisplay.dialog(prompt)
			} else {
				this.addSelectedDataset(dataset, org, closeSelector)
			}
		})
	},

	addSelectedDataset: function (dataset, org, closeSelector) {

		if (org) {
			this.organizationsById[org.id] = org
			nullSafePush(this.model, 'organizations', org)

			let project = unwrapModel(projects.get(org.projectId))
			this.projectsById[org.projectId] = project
			nullSafePush(this.model, 'projects', project)
		}

		if (this.datasetsById[dataset.id]) {
			closeSelector() // user just chose a dataset already included in the group permissions
		} else {
			var params = $.param({
				fields: true,
				permissions: true,
				linkedDatasets: true
			})
			var url = `api/datasets/${dataset.id}?${params}`
			$.get(url, dataset => {
				this.formView.change()

				// add permission for the current group to the dataset's
				// list of permissions
				this.addDatasetPermissionForGroupPreventingDuplicates(dataset)
				// add the dataset to the beginning of the list of datasets
				this.datasets = [dataset].concat(this.datasets)
				this.datasetsById[dataset.id] = dataset

				let datasetItemContext = this.groupDatasetsItemContext(
					dataset)
				this.$('.list').prepend(groupDatasetsItem(datasetItemContext))
				this.toggleNoneMessage()

				closeSelector()
			})
		}
	},

	/**
	 * Adds a dataset permission for the current group to the dataset's
	 * list of permissions. If there is already an existing permission for this
	 * group, it will be replaced with the new permission to prevent creating
	 * a duplicate permission (which can happen if the user deletes and then
	 * immediately re-adds the same dataset to the group dataset permissions)
	 * #51900 #52088
	 */
	addDatasetPermissionForGroupPreventingDuplicates: function (dataset) {
		dataset.permissions = dataset?.permissions || []
		let newPermissions = {
			principal: {
				type: 'group',
				id: this.model.id
			}
		}
		// find the index of an existing permission for this group, if it exists
		let idx = dataset.permissions.findIndex(p => {
			return p?.principal?.type === 'group' && p?.principal?.id == this.model.id
		})
		if (dataset.permissions[idx]) {
			// if there is an existing permission, replace the existing
			// permission with the newly added permission using the same id,
			// so this will be seen by DatasetPermissions.saveFiltersAndPermissions
			// as updating the permission instead of deleting and re-adding it
			// to prevent a DuplicateKeyException - #52088
			newPermissions.id = dataset.permissions[idx].id
			dataset.permissions[idx] = newPermissions
		} else {
			// add the new permission
			dataset.permissions.push(newPermissions)
		}
	},

	groupDatasetsItemContext: function (dataset) {
		const {id, organizationId, projectId} = dataset
		var modify = this._canModifyDataset(id)
		let orgAndProjectLabel = this.organizationsById[organizationId].label
		if (config.enableProjects) {
			// #34453 only show project label if projects are enabled
			orgAndProjectLabel += ` (${this.projectsById[projectId].label})`
		}
		return _.defaults({
			orgAndProjectLabel: orgAndProjectLabel,
			modify
		}, dataset)
	},

	edit: function(e) {
		var listItem = this.getListItem(e)

		let datasetId = listItem.data('id')
		if (this._canModifyDataset(datasetId)) {
			var datasetName = listItem.find('h3').text()
			var dataset = this.datasetsById[datasetId]
			let permissions = _.find(dataset.permissions, permission =>
				permission.principal.type === 'group' &&
				permission.principal.id == this.model.id)
			var datasetPermissionsForm = new DatasetPermissionsForm({
				model: new Dataset(dataset),
				permissionsLabel: _.trim(datasetName),
				permissions,
				treeItemsById: this.treeItemsById,
				groupType: this.options.groupType
			})
			appDisplay.dialog(datasetPermissionsForm)
			datasetPermissionsForm.on('done', function(newPermissions) {
				this.formView.change()
				_.extend(permissions, newPermissions)
			}.bind(this))
		} else {
			this._showNoPermissionTooltip(this.$(e.target))
		}
	},

	deleteConfirmation: function(e) {
		var listItem = this.getListItem(e)
		var datasetId = listItem.data('id')
		if (this._canModifyDataset(datasetId)) {
			this.deleteConfirmationTooltip = new DeleteConfirmationTooltip({
				message: i18n.get('common.group.removeDatasetPermission')
			}).render()
			this.tooltip = new Tooltip({
				content: this.deleteConfirmationTooltip.el,
				attachTo: $(e.currentTarget),
				placement: 'below',
				arrow: 'center',
				extraClass: 'delete-confirmation-tooltip large'
			}).render()
			this.listenTo(this.deleteConfirmationTooltip, 'cancel delete', () => {
				this.tooltip.remove()
			})
			this.listenTo(this.deleteConfirmationTooltip, 'delete', () => {
				this.delete(e)
			})
		} else {
			this._showNoPermissionTooltip(this.$(e.target))
		}
	},

	delete: function(e) {
		var listItem = this.getListItem(e)
		var datasetId = listItem.data('id')
		this.formView.change()

		delete this.datasetsById[datasetId]
		_.remove(this.datasets, function (dataset) {
			return dataset.id == datasetId
		})
		listItem.remove()
		// must go after removing the listItem
		this.toggleNoneMessage()
	},

	_showNoPermissionTooltip: function(el) {
		this._noPermissionTooltip = new Tooltip({
			attachTo: el,
			content: i18n.get('datasets.records.noPermission.modifyPermissions')
		}).render()
	},

	_canModifyDataset: function(datasetId) {
		let dataset = this.datasetsById[datasetId]
		let combinedPermissions = this._getCombinedPermissions(dataset)
		return combinedPermissions.administer ||
			combinedPermissions.modifyPermissions ||
			new Dataset(dataset).isModifyDataset()
	},

	/**
	 * Returns combined permissions for the current user for the current
	 * dataset which show the highest permissions available to the user. e.g.
	 * { administer: true, modifyRecords: true }
	 */
	_getCombinedPermissions: function(dataset) {
		let groupIdHash = arrayToHash(this.model.get('groupIds'))
		let permissions = _.filter(dataset.permissions, permission => {
			let principal = permission.principal
			return (principal.type === 'group' && groupIdHash[principal.id]) ||
				// #56351 compare the principal against the user, not
				// the impersonatorOrUser, so we are checking the correct
				// permissions (not the impersonator's permissions, if "log in
				// as" is being used)
				(principal.type === 'user' && principal.id == user.id)
		})
		let combinedPermission = {}
		let permissionKeyHash = arrayToHash(Dataset.PERMISSION_KEYS)
		_.each(permissions, permission => {
			let keys = _.keys(permission)
			_.each(keys, key => {
				if (permissionKeyHash[key] && permission[key]) {
					combinedPermission[key] = true
				}
			})
		})
		return combinedPermission
	},

	onClickExplore: function(e) {
		var listItem = this.getListItem(e)
		let datasetId = listItem.data('id')
		this.model.set('datasets', this.datasets)
		const continueFunction = () => conductor.overview('datasets', datasetId)
		guard.conditionally({
			continueFunction,
			errorFunction: error => {
				const topDialog = dialogStack.topViewByType(DependenciesPreventActionView)
				if (topDialog) {
					topDialog.on('save dont-save', continueFunction)
				}
			}
		})
	},

	getListItem: function(e) {
		return this.$(e.target).closest('[data-id]')
	},

	submit: function() {
		this.model.set('datasets', this.datasets)

		this.trigger('done')
	},

	remove() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.covertButtonTooltips, this._noPermissionTooltip,
		this.tooltip, this.deleteConfirmationTooltip], 'remove')
	}

})

/**
 * Adds the given value to the given array attribute in the Backbone model
 * @param {Backbone.Model} model, creating a new array if one doesn't exist.
 *
 * @param {String} attribute - the attribute name in the model that should
 * contain an array
 *
 * @param value - the value to add to the array
 */
function nullSafePush (model, attribute, value) {
	if (!model.get(attribute)) {
		model.set(attribute, [])
	}
	model.get(attribute).push(value)
}

export default GroupDatasets

GroupDatasets.__filename__ = 'admin/principals/groups/group-datasets.js'
